import { flightStatus } from '@trips/shared/flight-status';
import { formatTime } from '@trips/shared/format';
import type { FlightDetails, Item } from '@trips/shared/schema';
import { localDate } from '@trips/shared/time';
import { type ReactNode, useMemo, useState } from 'react';
import { selectLiveItems, selectPlaceLookup, selectPrefs } from '../../data/selectors';
import { useStore } from '../../data/store';
import { now } from '../../lib/clock';
import { phoneLink } from '../../lib/link';
import { Link, navigate } from '../../lib/router';
import { IconButton } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { Segmented } from '../../ui/Segmented';
import { StatusPill } from '../../ui/StatusPill';
import s from './FlightsScreen.module.css';

type View = 'upcoming' | 'past';

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });

function FlightRow({ item }: { item: Item }) {
  const places = useStore(selectPlaceLookup);
  const prefs = useStore(selectPrefs);
  const live = useStore((st) => st.tables.flightLive.get(item.id));
  const d = item.details as FlightDetails;
  const from = places(item.startPlaceId);
  const to = places(item.endPlaceId);
  const tz = item.startTz ?? from?.tz ?? 'UTC';
  const date = item.startAt != null ? localDate(item.startAt, tz) : null;
  const t = now();
  // Status only matters around the flight: from two days before until it's in.
  const fs =
    item.startAt != null &&
    (item.status === 'cancelled' ||
      (item.startAt - t < 48 * 3_600_000 && (item.endAt ?? item.startAt) + 3_600_000 > t))
      ? flightStatus(
          { startAt: item.startAt, endAt: item.endAt, cancelled: item.status === 'cancelled' },
          live,
          t,
          undefined,
          phoneLink(),
        )
      : null;
  const departs = fs?.depart.best ?? item.startAt;
  return (
    <Link to={`/flights/${item.id}`} className={s.row}>
      <span className={s.date}>
        {date ? (
          <>
            <span className={s.month}>{MONTH.format(new Date(`${date}T12:00:00Z`))}</span>
            <span className={`${s.day} num`}>{Number(date.slice(8))}</span>
          </>
        ) : (
          <span className={s.month}>No date</span>
        )}
      </span>
      <span className={s.main}>
        <span className={`${s.codes} wide`}>
          {from?.iata ?? d.origin}
          <Icon name="airplane-tilt" size={16} className={s.plane} />
          {to?.iata ?? d.destination}
        </span>
        <span className={s.number}>
          {d.airline} {d.flightNumber}
        </span>
      </span>
      <span className={s.right}>
        <span className={`${s.time} num`}>
          {departs != null ? formatTime(departs, tz, prefs) : ''}
        </span>
        {fs && (fs.tone !== 'ok' || fs.depart.moved || fs.live) && (
          <StatusPill label={fs.label} tone={fs.tone} />
        )}
      </span>
    </Link>
  );
}

const FOLD_KEY = 'trips.flightYears';

/** Which years I've opened or closed, remembered on this device. */
function readFolds(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(FOLD_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}

/** A year of past flights that opens and closes; the latest year starts open. */
function YearFold({
  year,
  count,
  openByDefault,
  children,
}: {
  year: string;
  count: number;
  openByDefault: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(() => readFolds()[year] ?? openByDefault);
  return (
    <section className={s.year}>
      <h2 className={s.yearHead}>
        <button
          type="button"
          className={`${s.yearButton} t-subhead muted`}
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
            try {
              localStorage.setItem(FOLD_KEY, JSON.stringify({ ...readFolds(), [year]: !open }));
            } catch {
              // Private mode: it just won't be remembered.
            }
          }}
        >
          <Icon name="caret-right" size={14} className={s.caret} />
          <span className={s.yearLabel}>{year}</span>
          <span className="num">{count}</span>
        </button>
      </h2>
      {open && children}
    </section>
  );
}

export function FlightsScreen() {
  const items = useStore(selectLiveItems);
  useStore((st) => st.minute);
  const [view, setView] = useState<View>('upcoming');
  const t = now();

  const { upcoming, pastByYear } = useMemo(() => {
    const flights = items.filter((i) => i.type === 'flight' && i.status !== 'cancelled');
    const upcoming = flights.filter((i) => (i.endAt ?? i.startAt ?? 0) >= t);
    const past = flights.filter((i) => (i.endAt ?? i.startAt ?? 0) < t).reverse();
    const years = new Map<string, Item[]>();
    for (const i of past) {
      const y = i.startAt != null ? String(new Date(i.startAt).getUTCFullYear()) : 'Undated';
      years.set(y, [...(years.get(y) ?? []), i]);
    }
    return { upcoming, pastByYear: [...years.entries()] };
  }, [items, t]);

  return (
    <div className={s.screen}>
      <header className={s.header}>
        <h1 className="t-title1">Flights</h1>
        <IconButton
          icon="chart-bar"
          label="Flight stats"
          onClick={() => {
            // Open Stats on the flight stats, not wherever it was left.
            try {
              localStorage.setItem('trips.statsView', 'flights');
            } catch {
              // Private mode: Stats opens on its default, which is flights.
            }
            navigate('/stats');
          }}
        />
      </header>
      <Segmented
        label="Which flights"
        value={view}
        onChange={setView}
        options={[
          { value: 'upcoming', label: 'Upcoming' },
          { value: 'past', label: 'Past' },
        ]}
      />
      {view === 'upcoming' ? (
        upcoming.length ? (
          <div className={s.rows}>
            {upcoming.map((i) => (
              <FlightRow key={i.id} item={i} />
            ))}
          </div>
        ) : (
          <EmptyState icon="airplane-tilt" title="No flights coming up" />
        )
      ) : pastByYear.length ? (
        pastByYear.map(([year, list], k) => (
          <YearFold key={year} year={year} count={list.length} openByDefault={k === 0}>
            <div className={s.rows}>
              {list.map((i) => (
                <FlightRow key={i.id} item={i} />
              ))}
            </div>
          </YearFold>
        ))
      ) : (
        <EmptyState icon="airplane-tilt" title="No past flights yet" />
      )}
    </div>
  );
}
